import { useState, useEffect } from 'react';
import { supabase } from "@/lib/supabase";

const API_URL = import.meta.env.VITE_API_URL;

export const useWebsiteAnalysis = (user) => {
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [error, setError] = useState('');
    const [analysisTypes, setAnalysisTypes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (user) {
            fetchAnalysisTypes()
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [user]);

    const fetchAnalysisTypes = async () => {
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) {
                setError('Please sign in to continue');
                return;
            }

            const response = await fetch(`${API_URL}/analysis-types`, {
                headers: {
                    'Authorization': `Bearer ${session.access_token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to fetch analysis types');
            }

            const { types } = await response.json();
            setAnalysisTypes(types);
            return types;
        } catch (err) {
            console.error('Error loading analysis types:', err);
            setError('Error loading analysis types');
            return [];
        }
    };

    const analyzeWebsites = async (file, selectedTypes, isDualAnalysis, analysisName) => {
        setIsAnalyzing(true);
        setError('');

        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) {
                throw new Error('Please sign in to continue');
            }

            const formData = new FormData();
            formData.append('file', file);
            formData.append('primaryAnalysisType', selectedTypes.primary);
            formData.append('analysisName', analysisName);
            if (isDualAnalysis) {
                formData.append('secondaryAnalysisType', selectedTypes.secondary);
            }

            const response = await fetch(`${API_URL}/analyze`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${session.access_token}`
                },
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Analysis failed');
            }

            const data = await response.json();
            return data.results;
        } catch (err) {
            setError(err.message || 'An error occurred during analysis');
            console.error('Analysis error:', err);
            throw err;
        } finally {
            setIsAnalyzing(false);
        }
    };

    return {
        isAnalyzing,
        error,
        setError,
        analysisTypes,
        isLoading,
        analyzeWebsites
    };
};